// Runtime-safe (only `Intl`; no npm or node: imports): used by Next, Vite and Deno edge functions.
//
// Dates and times follow the UI language's words ("14 Oct 2026" / "14 okt. 2026"). Money amounts
// deliberately do not: `formatAmount` in @financialharmony/ui stays in Swedish accounting format
// in every language, because `en-SE` formats a non-SEK currency the English way ("-€12.30") and a
// ledger with "1 234,50 kr" beside "€1,234.50" reads as two conventions. Revisit with the first
// customer whose market is not Swedish.
import { FORMATTING_LOCALES, type Locale } from "./locales.ts";

/** Falls back to plain English when the runtime ships no ICU data for a language's formatting locale. */
const FALLBACK_TAG = "en";

/**
 * The `Intl` tag to format with for a UI language. `fellBack` is true when the runtime has no data
 * for it (a stripped-down ICU build): callers that can raise the `i18n_icu_data_missing` alert
 * should, rather than letting dates quietly change shape.
 */
export function resolveFormattingLocale(locale: Locale): { tag: string; fellBack: boolean } {
  const tag = FORMATTING_LOCALES[locale];
  const supported = Intl.DateTimeFormat.supportedLocalesOf([tag]).length > 0;
  return supported ? { tag, fellBack: false } : { tag: FALLBACK_TAG, fellBack: true };
}

const DATE_STYLES = {
  /** "14 Oct 2026" / "14 okt. 2026" */
  date: { day: "numeric", month: "short", year: "numeric" },
  /** "14 Oct" / "14 okt." */
  dayMonth: { day: "numeric", month: "short" },
  /** "2026-10-14" in both languages: the Swedish accounting convention for periods, and unambiguous. */
  numeric: { year: "numeric", month: "2-digit", day: "2-digit" },
  /** "09:05" */
  time: { hour: "2-digit", minute: "2-digit" },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export type DateStyle = keyof typeof DATE_STYLES;

/** A date or time in the UI language's words with Swedish conventions; one place instead of a hardcoded locale per call site. */
export function formatDate(value: Date | string | number, locale: Locale, style: DateStyle = "date"): string {
  const { tag } = resolveFormattingLocale(locale);
  return new Intl.DateTimeFormat(tag, DATE_STYLES[style]).format(new Date(value));
}
